import { Link } from 'react-router-dom'
import PageHeader from '../../components/layout/PageHeader'
import useDeccaConversations from '../../hooks/decca/useDeccaConversations'
import useInfiniteScrollSentinel from '../../hooks/useInfiniteScrollSentinel'
import formatConversationDate from '../../helpers/decca/formatConversationDate'
import { client } from '../../hooks/Client'

// AI-CONTEXT: See docs/features/decca-gesprekken-overzicht.md

const PAGINA = 25

/**
 * Alle gesprekken die organisaties met Decca voerden, nieuwste bovenaan.
 *
 * De lijst laadt bij: er komen `PAGINA` gesprekken tegelijk binnen, en zodra de
 * onderkant in beeld komt wordt de volgende ronde opgehaald. Het ophalen
 * gebeurt dus ook echt per ronde — niet alles binnenhalen en daarna stukjes
 * tonen. De knop 'Meer laden' staat er als tweede weg naast, voor wie met het
 * toetsenbord werkt of in een browser zonder IntersectionObserver zit.
 *
 * Er zit met opzet geen zoekveld op. Een zoekterm zou alleen de al opgehaalde
 * gesprekken doorzoeken en bij geen treffer 'niets gevonden' melden, terwijl de
 * treffer een ronde verderop kan staan.
 *
 * Drie toestanden, in deze volgorde: nog niet bekend, mislukt, en pas dan de
 * inhoud. 'Nog geen gesprekken' is een bewering aan de lezer, en die is onwaar
 * zolang de gegevens onderweg zijn.
 * AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
 *
 * Een fout op een VERVOLGronde is geen lege pagina: de al geladen gesprekken
 * blijven staan en de melding komt eronder, met de knop ernaast om het opnieuw
 * te proberen. Anders wist één mislukte ronde alles wat er al stond — in
 * productie gebeurde dat toen het inlogtoken na een uur verliep en Cloud Run
 * één aanvraag met 401 weigerde: 200 geladen gesprekken werden vervangen door
 * één regel rode tekst.
 */
const DeccaConversationsOverview = () => {

    const { rows, loadingMore, error, hasMore, loadMore } = useDeccaConversations(PAGINA)

    // Na een fout laadt de lijst niet vanzelf verder: de markering staat dan in
    // beeld, dus automatisch bijladen zou het mislukkende verzoek in een lus
    // herhalen. De knop is dan de enige weg, en die drukt de lezer bewust in.
    const sentinelRef = useInfiniteScrollSentinel(loadMore, hasMore && !loadingMore && !error)

    const geladen = Array.isArray(rows)

    return (
        <div className='decca-conversations'>
            <PageHeader heading='Gesprekken met Decca' />

            {error && !geladen ? (
                <p className='decca-conversations__state decca-conversations__state--error'>{error}</p>
            ) : !geladen ? (
                <p className='decca-conversations__state'>Bezig met laden…</p>
            ) : rows.length === 0 ? (
                <p className='decca-conversations__state'>
                    Er zijn nog geen gesprekken met Decca gevoerd.
                </p>
            ) : (
                <>
                    <p className='decca-conversations__summary'>
                        {hasMore
                            ? `${rows.length} gesprekken geladen, er volgen er meer`
                            : `${rows.length} gesprekken`}
                    </p>

                    <ul className='decca-conversations__list'>
                        {rows.map((row) => (
                            <li key={row.id} className='decca-conversations__row'>
                                <Link
                                    className='decca-conversations__link'
                                    to={`/${client}/deccagesprekken/${row.id}`}
                                >
                                    <span className='decca-conversations__title'>
                                        {row.title || 'Gesprek zonder titel'}
                                    </span>
                                    <span className='decca-conversations__org'>
                                        {row.communityName || 'Onbekende organisatie'}
                                    </span>
                                    <span className='decca-conversations__date'>
                                        {formatConversationDate(row.timestampMs)}
                                    </span>
                                </Link>
                            </li>
                        ))}
                    </ul>

                    {error && (
                        <p className='decca-conversations__state decca-conversations__state--error'>{error}</p>
                    )}

                    <div ref={sentinelRef} className='decca-conversations__sentinel'>
                        {loadingMore && (
                            <span className='decca-conversations__state'>Meer gesprekken laden…</span>
                        )}
                        {!loadingMore && hasMore && (
                            <button
                                type='button'
                                className='decca-conversations__more'
                                onClick={loadMore}
                            >
                                {error ? 'Opnieuw proberen' : 'Meer laden'}
                            </button>
                        )}
                    </div>
                </>
            )}
        </div>
    )
}

export default DeccaConversationsOverview
